import "../../styles/tailwind.css";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { convertCartToPurchaseData, readPurchasedCart } from "../../modules/checkoutHelpers";
import { sendSlackMessageTracked } from "../../models/notifications";

const REDIRECT_DELAY = 5; // seconds before auto-redirect to dashboard

export default function OrderStatusComponent() {
    const navigate = useNavigate();
    const [status, setStatus] = useState("loading"); // "loading" | "success" | "failed"
    const [planName, setPlanName] = useState(null);
    const [countdown, setCountdown] = useState(REDIRECT_DELAY);

    useEffect(() => {
        // Stripe appends ?redirect_status=succeeded&payment_intent=pi_xxx to the return_url
        const params = new URLSearchParams(window.location.search);
        const redirectStatus = params.get("redirect_status");

        if (redirectStatus === "succeeded") {
            // Move cart → purchased_cart so the data is preserved
            convertCartToPurchaseData();
            readPurchasedCart((data) => {
                const firstItem = data?.productLineItems?.[0];
                if (firstItem) setPlanName(firstItem.name ?? null);
            });
            setStatus("success");
            sendSlackMessageTracked("4️⃣ Payment succeeded — visitor on status page.");
        } else if (redirectStatus === "failed" || redirectStatus === "canceled") {
            setStatus("failed");
            sendSlackMessageTracked(`4️⃣ Payment ${redirectStatus} — visitor on status page.`);
        } else {
            // No Stripe params — treat as success for non-redirect payment methods
            // (card payments that don't require 3DS land here without a redirect)
            setStatus("success");
            sendSlackMessageTracked("4️⃣ Visitor on status page (no redirect params).");
        }
    }, []);

    // Countdown + auto-redirect on success
    useEffect(() => {
        if (status !== "success") return;
        if (countdown <= 0) {
            window.location.href = "https://app.pabloo.com/overview";
            return;
        }
        const t = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(t);
    }, [status, countdown, navigate]);

    if (status === "loading") {
        return (
            <main style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: "32px", height: "32px", border: "3px solid #e5e7eb", borderTopColor: "#22c55e", borderRadius: "50%", animation: "spin 0.8s linear infinite" }}/>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </main>
        );
    }

    if (status === "failed") {
        return (
            <main style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
                <div style={{ textAlign: "center", maxWidth: "420px" }}>
                    <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "#fef2f2", border: "2px solid #fecaca", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
                        <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#dc2626" strokeWidth="2.2" strokeLinecap="round">
                            <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                    </div>
                    <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#111827", marginBottom: "10px" }}>Payment not completed</h1>
                    <p style={{ fontSize: "14px", color: "#6b7280", lineHeight: 1.6, marginBottom: "28px" }}>
                        Something went wrong processing your payment. No charge was made.
                    </p>
                    <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                        <button
                            onClick={() => navigate("/payment")}
                            style={{ padding: "11px 24px", fontSize: "13px", fontWeight: 600, background: "#111827", color: "#fff", border: "none", borderRadius: "9px", cursor: "pointer" }}
                        >
                            Try again
                        </button>
                        <button
                            onClick={() => navigate("/plans")}
                            style={{ padding: "11px 24px", fontSize: "13px", fontWeight: 500, background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: "9px", cursor: "pointer" }}
                        >
                            Back to plans
                        </button>
                    </div>
                </div>
            </main>
        );
    }

    // Success
    return (
        <main style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 24px", background: "#f9fafb" }}>
            <div style={{ textAlign: "center", maxWidth: "460px" }}>
                {/* Check mark */}
                <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "#dcfce7", border: "2px solid #86efac", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px" }}>
                    <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                    </svg>
                </div>

                <p style={{ fontSize: "12px", fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "10px" }}>
                    Payment confirmed
                </p>
                <h1 style={{ fontSize: "28px", fontWeight: 800, color: "#111827", marginBottom: "12px", lineHeight: 1.2 }}>
                    Welcome to Pabloo{planName ? ` ${planName}` : ""}!
                </h1>
                <p style={{ fontSize: "14px", color: "#6b7280", lineHeight: 1.7, marginBottom: "36px" }}>
                    Your subscription is active. Head to your dashboard to connect your Shopify store and launch your first campaign.
                </p>

                {/* Primary CTA */}
                <button
                    onClick={() => { window.location.href = "https://app.pabloo.com/overview"; }}
                    style={{
                        padding: "13px 36px", fontSize: "14px", fontWeight: 700,
                        background: "linear-gradient(135deg, #16a34a, #22c55e)",
                        color: "#fff", border: "none", borderRadius: "10px", cursor: "pointer",
                        boxShadow: "0 4px 14px rgba(34,197,94,0.35)", marginBottom: "16px",
                        display: "block", width: "100%",
                    }}
                >
                    Go to Dashboard
                </button>

                <p style={{ fontSize: "12px", color: "#9ca3af" }}>
                    Redirecting automatically in {countdown}s…
                </p>
            </div>
        </main>
    );
}
